import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BwjPageContent } from '../../../../framework/components/bwj-page-content';
import { BwjPageHeader } from '../../../../framework/components/bwj-page-header';
import { CustomerAccountService, useProjectConfiguration } from '../../../../services/customer-account';
import { AppRoutes, useProjectInstallationSite } from '../../../app-routes';
import { ProjectService } from '../../../services/project';
import { DoorType } from '../../../../entities-generated/common/door-type';
import { useNavigator } from '../../../../framework/services/navigation';
import { InstallationState } from '../../../../entities-generated/common/installation-state';
import * as SelectionPageServices from '../../services/selection-page';
import { getTheme, MessageBarType } from '@fluentui/react';
import { DoorRecommendation } from '../../../../entities-generated/common/door-recommendation';
import { IDoorInstallationModel, DoorInstallationModel } from '../../../../entities-generated/models/project/door-installation-model';
import { useStandardErrorState } from '../../../../entities/view-manager/error';
import { IShowAnnouncementBannerArgs } from '../../../../framework/components/bwj-announcement/props';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { BwjEventArgs } from '../../../../framework/entities/bwj-event';
import { orderByDesc, where, firstOrDefault } from '../../../../framework/utils/collection';
import { IStormDoorHandleModel } from '../../../../entities-generated/models/storm-door/storm-door-handle-model';
import { ShopStormHandleApiClient } from '../../../../services/shop-storm-door-handle';
import { IProductTexture } from '../../../../entities-generated/common/product-texture';
import { IProjectSelectionRequest } from '../../../../entities-generated/models/project/project-selection-request';
import { ToastService } from '../../../../framework/services/toast';
import { ShoppingIntroDialog } from '../../components/shop-intro-dialog';
import { IStormHandleDetailPanelArgs, StormHandleDetailPanel } from '../../components/st-handle-detail-panel';
import { BwjAnnouncementBanner } from '../../../../framework/components/bwj-announcement';
import { toTitleText } from '../../../../framework/utils/string';
import { ProductTileContainer } from '../../components/product-tile-container';
import { StormHandleTile } from '../../components/st-handle-tile';
import { isThisLocalhost } from '../../../../framework/utils';
import { TutorialService } from '../../../services/tutorial';

const debuggin = isThisLocalhost();

const subscriberId = 'Page.ShopStormHandles';

export const PageShopStormHandles: React.FunctionComponent = () => {
  const theme = getTheme();
  const cs = SelectionPageServices.getStyles(theme);
  const nav = useNavigator();
  const location = useProjectInstallationSite();
  const projectConfig = useProjectConfiguration(subscriberId);
  const errorState = useStandardErrorState();
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('Loading door handle options...');
  const installation = useRef<IDoorInstallationModel>(new DoorInstallationModel());
  const previousSelectionMade = useRef(false);
  const initialLoad = useRef(true);
  const [showIntroDialog, setShowIntroDialog] = useState(false);
  const [rawHandleOptions, setRawHandleOptions] = useState<IStormDoorHandleModel[]>([]);
  const [launchHandleDetailsPanel, setLaunchHandleDetailsPanel] = useState<BwjEventArgs<IStormHandleDetailPanelArgs> | undefined>(undefined);
  const [invokeShowSaveError, setInvokeShowSaveError] = useState<BwjEventArgs<IShowAnnouncementBannerArgs | undefined> | undefined>(undefined);
  const handleOptions = useMemo(() => {
    return orderByDesc(rawHandleOptions, h => h.weight);
  }, [rawHandleOptions]);
  const recommendedOptions = useMemo(() => {
    const options = where(rawHandleOptions, d => d.recommendation !== DoorRecommendation.Standard);
    return orderByDesc(options, d => d.recommendation);
  }, [rawHandleOptions]);
  
  const showSaveError = () => {
    setInvokeShowSaveError(new BwjEventArgs(SelectionPageServices.saveErrorArgs));
    window.setTimeout(() => setInvokeShowSaveError(new BwjEventArgs(undefined)), 5000);
  }

  const showLoading = (message: string) => {
    loadingMessage.current = message;
    setViewState(BwjViewState.Loading);
  }

  const refreshHandleOptions = useCallback(() => {
    const loadMsg = loadingMessage.current !== 'Loading best door options...'
    ? 'Updating your options...' : loadingMessage.current;
    showLoading(loadMsg);

    ShopStormHandleApiClient.getStormDoorHandleOptions(installation.current.id)
    .then(resp => {
      setRawHandleOptions(resp);
      const vs = resp.length > 0 ? BwjViewState.Ready : BwjViewState.NoSearchResults;
      setViewState(vs);

      if(initialLoad.current && previousSelectionMade.current === false) {
        initialLoad.current = false;
        setShowIntroDialog(true);
      }
    })
    .catch(() => {
      setViewState(BwjViewState.Error);
    });
  }, []);

  useEffect(() => {
    const doorInstall = ProjectService.getDoorInstallation(location, DoorType.StormDoor, projectConfig);
    if(!doorInstall // no such install
      || doorInstall.isExcluded
      || doorInstall.selectHandle === false
      || (doorInstall.installationState !== InstallationState.NeedsHandleSelection
        && doorInstall.installationState !== InstallationState.ConfigurationComplete)) {
      // We go home instead of just going directly to the next logical page.  This is done intentionally so that, in the case of consecutive
      // handle choices, this instance of the page is destroyed and not reused
      nav.goTo(AppRoutes.home.url());
      return;
    }

    installation.current = doorInstall;
    previousSelectionMade.current = !!doorInstall.selectedHandle;
    refreshHandleOptions();

  }, [location, projectConfig, nav, refreshHandleOptions]);
  
  const onViewHandleDetails = (handle: IStormDoorHandleModel) => {
    debuggin && console.log(handle);
    const itl = installation.current;
    const variantId = itl.selectedHandle?.id === handle.id ? itl.selectedHandle.textureVariant.id : null;
    const selected = firstOrDefault(handle.textureVariants, v => v.id === variantId);
    const args = { handle, selected };
    setLaunchHandleDetailsPanel(new BwjEventArgs(args));
  }

  const onHandleSelected = (handle: IStormDoorHandleModel, variant: IProductTexture) => {
    showLoading('Saving your selection...');

    const request: IProjectSelectionRequest = {
      doorInstallationId: installation.current.id,
      selectionId: {
        productId: handle.id,
        variantId: variant.id
      }
    };
    ShopStormHandleApiClient.selectStormDoorHandle(request)
    .then(resp => {
      CustomerAccountService.updateProjectConfiguration(resp);
      ToastService.show(MessageBarType.success, `Selected ${handle.description}`);
      nav.goTo(AppRoutes.home.url());
    })
    .catch(() => {
      setViewState(BwjViewState.Ready);
      showSaveError();
    });
  };

  const onCloseShoppingIntroDialog = () => {
    setShowIntroDialog(false);
    TutorialService.teachShoppingCart();
  }

  const themeColor = theme.palette.themePrimary;
  return (
    <>
    <ShoppingIntroDialog
      showDialog={showIntroDialog}
      title={'Customize Your Door'}
      richContent={<span>The storm door you selected for the <strong style={{ color: themeColor}}>{location.replaceAll('-', ' ')}</strong> includes a handle of your choice.  Pick one that speaks to your sense of style!</span>}
      onClose={onCloseShoppingIntroDialog}
    />
    <StormHandleDetailPanel
      launchPanel={launchHandleDetailsPanel}
      onHandleSelected={onHandleSelected}
    />
    <BwjViewManager
      state={viewState}
      loadingState={{
        message: loadingMessage.current,
      }}
      errorState={errorState}
      noSearchResultsState={{
        icon: 'SearchAndApps',
        message: 'Uh oh... No handles found. Please call us for assistance - (585) 576-1654',
      }}
    >
      <BwjAnnouncementBanner
        invokeShowAnnouncement={invokeShowSaveError}
        closeDialogButtonText={'Close'}
        ariaCloseBannerMessage={'Dismiss message'}
        ariaShowFullMessageDialog={'Show full message'}
      />
      <BwjPageHeader
        title={`Door Handle - ${toTitleText(location)}`}
      />
      <BwjPageContent contentDescription={'primary content (no subcontent)'}>
        {recommendedOptions.length > 0 &&
        <div className={cs.recommendedSection}>
          <h2 className={cs.recommendedHeading}>
            <span>Recommended</span>
            <span className='subtext'>based on your previous selections</span>
          </h2>
          <ProductTileContainer>
            {recommendedOptions.map(handle =>
              <StormHandleTile
                key={handle.id}
                idPrefix='recmd-tile'
                handle={handle}
                onSelected={onViewHandleDetails}
                recommendation={SelectionPageServices.recommendedSectionRecommendation(handle.recommendation)}
              />
            )}
          </ProductTileContainer>
        </div>}

        {recommendedOptions.length > 0 &&
        <h2 className={cs.generalHeading}>
          All Handle Options
        </h2>}
        <ProductTileContainer>
          {handleOptions.map(handle =>
            <StormHandleTile
              key={handle.id}
              idPrefix='recmd-tile'
              handle={handle}
              onSelected={onViewHandleDetails}
              recommendation={SelectionPageServices.recommendedSectionRecommendation(handle.recommendation)}
            />
          )}
        </ProductTileContainer>
      </BwjPageContent>
    </BwjViewManager>
    </>
  );
};